import React, { useState, useEffect } from "react";
import "./BusLayout.css";

const BusLayout = ({ seatData, onSeatSelect, selectedSeats = [] }) => {
  const [seats, setSeats] = useState([]);
  const [layoutType, setLayoutType] = useState("");

  useEffect(() => {
    if (seatData && seatData.seats) {
      const processedSeats = processSeatData(seatData.seats);
      setSeats(processedSeats);
      setLayoutType(determineLayoutType(processedSeats));
    }
  }, [seatData]);

  const processSeatData = (seats) => {
    return seats.map(seat => ({
      ...seat,
      row: seat.row || 0,
      column: seat.column || 0,
      seatType: seat.seatType || 0,
      zIndex: seat.zIndex || 0,
      length: seat.length || 1,
      width: seat.width || 1
    }));
  };

  const determineLayoutType = (seats) => {
    if (!seats || seats.length === 0) return "seater";

    const hasUpperBerths = seats.some((seat) => seat.seatType === 1);
    const hasLowerBerths = seats.some((seat) => seat.seatType === 0);
    const hasSleeper = seats.some((seat) => seat.sleeper);
    const hasLength2 = seats.some((seat) => seat.length === 2);
    const hasWidth2 = seats.some((seat) => seat.width === 2);

    if (hasUpperBerths && hasLowerBerths && hasLength2 && hasWidth2) {
      return "sleeper";
    } else if (hasUpperBerths && hasLowerBerths && hasLength2) {
      return "sleeper";
    } else if (hasSleeper && (hasLength2 || hasWidth2)) {
      return "sleeper";
    } else {
      return "seater";
    }
  };

  const getSeatStatus = (seat) => {
    if (!seat.available || seat.bookedBy) return "booked";
    if (selectedSeats.some((s) => s.id === seat.id)) return "selected";
    return "available";
  };

  const getSeatDisplayInfo = (seat) => {
    const isUpper = seat.seatType === 1;
    const isLadiesSeat = seat.ladiesSeat;
    const isMalesSeat = seat.malesSeat;
    const isSleeper = seat.sleeper;
    const isLength2 = seat.length === 2;
    const isWidth2 = seat.width === 2;

    let icon = "💺";
    let typeLabel = "Seater";

    if (isSleeper) {
      icon = isUpper ? "🛏️" : "🛌";
      typeLabel = isUpper ? "Upper Sleeper" : "Lower Sleeper";
    } else if (isLadiesSeat) {
      icon = "👩";
      typeLabel = "Ladies Seat";
    } else if (isMalesSeat) {
      icon = "👨";
      typeLabel = "Mens Seat";
    }

    return { 
      icon, 
      typeLabel, 
      isUpper, 
      isLadiesSeat, 
      isMalesSeat, 
      isSleeper,
      isLength2,
      isWidth2
    };
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR${Math.round(num)}`;
  };

  const renderSeat = (seat, additionalClass = "") => {
    const status = getSeatStatus(seat);
    const seatInfo = getSeatDisplayInfo(seat);
    const seatPrice = seat.totalPriceWithTaxes || seat.totalFare || 0;

    return (
      <div
        key={seat.id}
        className={`seat ${status} ${additionalClass} ${
          seatInfo.isLadiesSeat ? "ladies" : ""
        } ${seatInfo.isMalesSeat ? "males" : ""} ${
          seatInfo.isUpper ? "upper" : "lower"
        } ${seatInfo.isSleeper ? "sleeper" : "seater"}`}
        onClick={() => onSeatSelect(seat)}
        title={`${seat.id} - ${seatInfo.typeLabel} - ${formatCurrency(seatPrice)}`}
      >
        <div className="seat-icon">{seatInfo.icon}</div>
        <div className="seat-number">{seat.id}</div>
        <div className="seat-price">{formatCurrency(seatPrice)}</div>
        {status === "selected" && <div className="selected-tick">✓</div>}
        {status === "booked" && <div className="booked-cross">✗</div>}
      </div>
    );
  };

  const renderSeaterLayout = () => {
    const rows = {};
    seats.forEach(seat => {
      if (!rows[seat.row]) rows[seat.row] = [];
      rows[seat.row].push(seat);
    });

    return (
      <div className="layout-seater">
        <div className="bus-aisle">
          <div className="aisle-label">AISLE</div>
        </div>
        <div className="seater-grid">
          {Object.keys(rows).sort((a, b) => a - b).map(rowKey => (
            <div key={rowKey} className="seat-row">
              <div className="row-label">Row {parseInt(rowKey) + 1}</div>
              <div className="row-seats">
                {rows[rowKey].sort((a, b) => a.column - b.column).map(seat => 
                  renderSeat(seat)
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSleeperLayout = () => {
    const lowerBerths = seats.filter(seat => seat.seatType === 0);
    const upperBerths = seats.filter(seat => seat.seatType === 1);

    return (
      <div className="layout-sleeper">
        <div className="berth-section">
          <div className="berth-column lower-berths">
            <h4>Lower Berths</h4>
            <div className="berth-grid">
              {lowerBerths.map(seat => renderSeat(seat, "lower-berth"))}
            </div>
          </div>
          <div className="berth-column upper-berths">
            <h4>Upper Berths</h4>
            <div className="berth-grid">
              {upperBerths.map(seat => renderSeat(seat, "upper-berth"))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderHorizontalSleeperLayout = () => {
    const rows = {};
    seats.forEach(seat => {
      if (!rows[seat.row]) rows[seat.row] = [];
      rows[seat.row].push(seat);
    });

    return (
      <div className="layout-horizontal-sleeper">
        <div className="bus-aisle">
          <div className="aisle-label">AISLE</div>
        </div>
        <div className="sleeper-grid">
          {Object.keys(rows).sort((a, b) => a - b).map(rowKey => (
            <div key={rowKey} className="sleeper-row">
              <div className="row-label">Row {parseInt(rowKey) + 1}</div>
              <div className="row-seats">
                {rows[rowKey].sort((a, b) => a.column - b.column).map(seat => 
                  renderSeat(seat, seat.length === 2 ? "long-seat" : "")
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLayout = () => {
    switch (layoutType) {
      case "sleeper":
        return renderSleeperLayout();
      default:
        return renderSeaterLayout();
    }
  };

  if (!seats || seats.length === 0) {
    return (
      <div className="bus-layout">
        <div className="no-seats-message">
          <p>No seat data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bus-layout">
      <div className="driver-section">
        <div className="driver-cabin">
          <span className="driver-icon">🚗</span>
          <span>Driver Cabin</span>
        </div>
        <div className="bus-direction">
          <span className="arrow">↓</span>
          <span>Front of Bus</span>
        </div>
      </div>

      <div className="seat-layout-container">
        {renderLayout()}
      </div>

     
    </div>
  );
};



export default BusLayout;